<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Precios Base</h1>
    </div>

    <div class="table-container custom-form">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">

          <!-- Toggle de dirección -->
          <div class="d-flex mb-2 mb-md-0">
            <button
              type="button"
              :class="['button', routeType === 'district_to_airport' ? 'button-primary' : 'button-light']"
              @click="setRouteType('district_to_airport')"
            >
              Distrito → Aeropuerto
            </button>
            <button
              type="button"
              :class="['button ml-2', routeType === 'airport_to_district' ? 'button-primary' : 'button-light']"
              @click="setRouteType('airport_to_district')"
            >
              Aeropuerto → Distrito
            </button>
          </div>

          <!-- Buscador y acciones -->
          <div class="d-flex align-items-center">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover title="Recargar"
                @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>
            <SearchBar placeholder="Buscar distrito..." @search="searchText = $event" @input="searchText = $event" class="ml-md-2" />
            <button
              type="button"
              class="button button-primary ml-2"
              :disabled="!hasChanges"
              @click="openSaveModal"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>

      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Distrito</th>
              <th class="text-center">Sedan</th>
              <th class="text-center">SUV</th>
              <th class="text-center">VAN</th>
              <th class="text-center">Sprinter</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredRows.length === 0">
              <td colspan="5" class="text-center">No se encontraron distritos</td>
            </tr>
            <tr v-for="row in filteredRows" :key="row.districtId">
              <td class="pl-4">{{ row.districtName }}</td>
              <td v-for="vt in vehicleTypes" :key="vt" class="text-right" style="width: 130px;">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  class="custom-form-control text-right"
                  :class="{'custom-form-control-error': isInvalid(row.districtId, vt)}"
                  :value="getAmount(row.districtId, vt)"
                  @input="setAmount(row.districtId, vt, $event.target.value)"
                  style="min-width: 90px;"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="saveChanges"
    />
  </div>
</template>

<script>
import { BasePriceService } from '@/core/services'
import { Alert, ConfirmationModal, ConfirmationModalMixin, SearchBar } from 'wize-admin'

const VEHICLE_TYPES = ['Sedan', 'SUV', 'VAN', 'Sprinter']

export default {
  components: { ConfirmationModal, SearchBar },
  mixins: [ConfirmationModalMixin],
  data() {
    return {
      vehicleTypes: VEHICLE_TYPES,
      routeType: 'district_to_airport',
      searchText: '',
      // Lista de distritos (id, name) — se carga una sola vez
      districts: [],
      // Mapa de precios originales: key = "districtId|vehicleType" → amount
      originalPrices: {},
      // Mapa de precios editados: misma key → amount (string del input)
      editedPrices: {},
      // Set de keys con valor inválido (vacío o ≤ 0)
      invalidKeys: new Set(),
    }
  },
  computed: {
    filteredRows() {
      const q = this.searchText.trim().toLowerCase()
      const PRIORITY = ['Miraflores', 'Barranco', 'San Isidro', 'Santiago de Surco', 'La Molina']

      const hasPrices = (districtId) =>
        this.vehicleTypes.some(vt => (this.originalPrices[this.key(districtId, vt)] ?? 0) > 0)

      const priorityIndex = (name) => {
        const i = PRIORITY.indexOf(name)
        return i >= 0 ? i : Infinity
      }

      return this.districts
        .filter(d => !q || d.name.toLowerCase().includes(q))
        .map(d => ({ districtId: d.id, districtName: d.name }))
        .sort((a, b) => {
          const pa = priorityIndex(a.districtName)
          const pb = priorityIndex(b.districtName)
          if (pa !== Infinity && pb !== Infinity) return pa - pb
          if (pa !== Infinity) return -1
          if (pb !== Infinity) return 1
          const ha = hasPrices(a.districtId)
          const hb = hasPrices(b.districtId)
          if (ha && !hb) return -1
          if (!ha && hb) return 1
          return a.districtName.localeCompare(b.districtName)
        })
    },
    hasChanges() {
      return Object.keys(this.editedPrices).some(key => {
        const raw = this.editedPrices[key]
        const val = parseFloat(raw)
        const original = this.originalPrices[key] ?? 0
        // Borrado: vacío o 0 cuando antes había precio
        if ((raw === '' || raw === '0' || val === 0) && original > 0) return true
        // Upsert: valor positivo distinto al original
        return !isNaN(val) && val > 0 && val !== original
      })
    },
  },
  methods: {
    key(districtId, vehicleType) {
      return `${districtId}|${vehicleType}`
    },
    getAmount(districtId, vehicleType) {
      const k = this.key(districtId, vehicleType)
      if (k in this.editedPrices) return this.editedPrices[k]
      const val = this.originalPrices[k] ?? ''
      return val > 0 ? parseFloat(val).toFixed(2) : val
    },
    setAmount(districtId, vehicleType, value) {
      const k = this.key(districtId, vehicleType)
      this.$set(this.editedPrices, k, value)
      const num = parseFloat(value)
      // Solo inválido si es explícitamente negativo
      if (!isNaN(num) && num < 0) {
        this.invalidKeys.add(k)
      } else {
        this.invalidKeys.delete(k)
      }
    },
    isInvalid(districtId, vehicleType) {
      return this.invalidKeys.has(this.key(districtId, vehicleType))
    },
    setRouteType(rt) {
      if (rt === this.routeType) return
      this.routeType = rt
      this.editedPrices = {}
      this.invalidKeys = new Set()
      this.loadPrices()
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const [distRes, priceRes] = await Promise.all([
          BasePriceService.getDistricts(),
          BasePriceService.getBasePrices(this.routeType),
        ])
        this.districts = distRes.payload || []
        this._applyPrices(priceRes.payload || [])
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar los datos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadPrices() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await BasePriceService.getBasePrices(this.routeType)
        this._applyPrices(res.payload || [])
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar los precios')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    _applyPrices(items) {
      const map = {}
      items.forEach(item => {
        map[this.key(item.district_id, item.vehicle_type)] = item.base_amount
      })
      this.originalPrices = map
      this.editedPrices = {}
      this.invalidKeys = new Set()
    },
    openSaveModal() {
      if (this.invalidKeys.size > 0) {
        Alert.error('Corrige los valores inválidos antes de guardar')
        return
      }
      this.openConfirmationModal({
        title: 'Guardar cambios',
        description: '¿Está seguro que desea guardar los cambios en los precios base?',
        acceptText: 'Guardar',
        action: 'save',
      })
    },
    closeConfirmationModal() {
      this.confirmationModal.show = false
    },
    async saveChanges() {
      this.closeConfirmationModal()

      const items = []
      for (const [k, rawVal] of Object.entries(this.editedPrices)) {
        const num = parseFloat(rawVal)
        const original = this.originalPrices[k] ?? 0
        const isEmpty = rawVal === '' || rawVal === '0' || num === 0

        if (isEmpty) {
          // Borrado: solo si había precio antes
          if (original > 0) {
            const [districtId, vehicleType] = k.split('|')
            items.push({ district_id: districtId, vehicle_type: vehicleType, route_type: this.routeType, base_amount: 0 })
          }
          continue
        }

        if (isNaN(num) || num < 0) continue  // negativo o inválido
        if (num === original) continue         // sin cambio

        const [districtId, vehicleType] = k.split('|')
        items.push({ district_id: districtId, vehicle_type: vehicleType, route_type: this.routeType, base_amount: num })
      }

      if (items.length === 0) {
        Alert.error('No hay cambios para guardar')
        return
      }

      try {
        await this.$store.dispatch('app/loading', true)
        const res = await BasePriceService.updateBasePrices(items)
        const results = res.payload || []
        const errors = results.filter(r => r.error)
        const updated = results.filter(r => r.updated && r.new_amount > 0)
        const deleted = results.filter(r => r.updated && r.new_amount === 0)

        if (errors.length > 0) {
          Alert.error(`${errors.length} precio(s) no pudieron procesarse`)
        } else {
          const parts = []
          if (updated.length > 0) parts.push(`${updated.length} precio(s) actualizados`)
          if (deleted.length > 0) parts.push(`${deleted.length} precio(s) eliminados`)
          Alert.success(parts.join(', '))
        }

        await this.loadPrices()
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al guardar los cambios')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="stylus" scoped></style>
